import {
  DocumentLockedError,
  EnvelopeError,
  KeyRefusedError,
  KeyServiceUnavailableError,
  NoLauncherError,
  SignInRequiredError,
  type EnvelopeFailure,
} from './errors';
import {
  GENERIC_FAILURE,
  LOAN_TABLE_GENERIC_FAILURE,
  LOAN_TABLE_SIGN_IN_AGAIN,
  SIGN_IN_AGAIN,
  describeLoanTableRefusal,
  describeRefusal,
} from './refusalMessages';

import type { FailureDescription, LockReason } from './types';

const NOT_A_GENUINE_COPY: FailureDescription = {
  title: "This file isn't a genuine copy",
  body: "It didn't come from ORSNN, or it has been changed. Download your copy again from the sale's data room.",
  next: 'none',
};

// Header rules run after the signature verified: ORSNN made this copy, so never call it fake.
const UNSUPPORTED_FORMAT: FailureDescription = {
  title: "This copy can't be opened on this page",
  body: "Its format doesn't match this version of the page. Reload the page and open the file again. If that doesn't help, download a new copy from the sale's data room.",
  next: 'none',
};

const DAMAGED_COPY: FailureDescription = {
  title: 'This copy is damaged',
  body: "Download it again from the sale's data room.",
  next: 'none',
};

const MESSAGE_BY_ENVELOPE_FAILURE: Record<EnvelopeFailure, FailureDescription> = {
  signature: NOT_A_GENUINE_COPY,
  header: UNSUPPORTED_FORMAT,
  tag: DAMAGED_COPY,
  length: DAMAGED_COPY,
  file_hash: DAMAGED_COPY,
};

const NOT_OPENED_FROM_FILE: FailureDescription = {
  title: 'Open this document from your downloaded file',
  body: 'Open the protected file you downloaded and click Open. This window shows a document only when that file opens it.',
  next: 'none',
};

const ACCESS_SERVICE_UNREACHABLE: FailureDescription = {
  title: "We can't reach the access service",
  body: 'Check your connection, then try again.',
  next: 'retry',
};

const VIEWING_TIME_OVER: FailureDescription = {
  title: 'Your viewing time is over',
  body: 'The time allowed for viewing this document has ended. Reopen the file if you still have access to the sale.',
  next: 'none',
};

const CONNECTION_LOST: FailureDescription = {
  title: 'The document was closed',
  body: 'We lost the connection that keeps your access confirmed. Check your connection, then reopen the file.',
  next: 'retry',
};

const DOCUMENT_CLOSED: FailureDescription = {
  title: 'The document was closed',
  body: 'Reopen the file to view it again.',
  next: 'retry',
};

export function describeFailure(err: unknown): FailureDescription {
  return { ...failureDescriptionFor(err) };
}

function failureDescriptionFor(err: unknown): FailureDescription {
  if (err instanceof KeyRefusedError) return describeRefusal(err.reason);
  if (err instanceof DocumentLockedError) return describeLock(err.lock);
  if (err instanceof SignInRequiredError) return SIGN_IN_AGAIN;
  if (err instanceof EnvelopeError) return MESSAGE_BY_ENVELOPE_FAILURE[err.reason];
  if (err instanceof NoLauncherError) return NOT_OPENED_FROM_FILE;
  if (err instanceof KeyServiceUnavailableError) return ACCESS_SERVICE_UNREACHABLE;
  return GENERIC_FAILURE;
}

function describeLock(lock: LockReason): FailureDescription {
  switch (lock.kind) {
    case 'refused':
      return describeRefusal(lock.reason);
    case 'expired':
      return VIEWING_TIME_OVER;
    case 'unreachable':
      return CONNECTION_LOST;
    case 'aborted':
      return DOCUMENT_CLOSED;
  }
}

const LOAN_TABLE_TIME_OVER: FailureDescription = {
  title: 'Your viewing time is over',
  body: "The time allowed for viewing this loan data has ended. Open the pool's loans again if you still have access to the sale.",
  next: 'retry',
};

const LOAN_TABLE_CONNECTION_LOST: FailureDescription = {
  title: 'The loan data was closed',
  body: 'We lost the connection that keeps your access confirmed. Check your connection, then open the loan data again.',
  next: 'retry',
};

const LOAN_TABLE_CLOSED: FailureDescription = {
  title: 'The loan data was closed',
  body: "Open the pool's loans again to view them.",
  next: 'retry',
};

export function describeLoanTableFailure(err: unknown): FailureDescription {
  return { ...loanTableFailureFor(err) };
}

function loanTableFailureFor(err: unknown): FailureDescription {
  if (err instanceof KeyRefusedError) return describeLoanTableRefusal(err.reason);
  if (err instanceof DocumentLockedError) return describeLoanTableLock(err.lock);
  if (err instanceof SignInRequiredError) return LOAN_TABLE_SIGN_IN_AGAIN;
  if (err instanceof KeyServiceUnavailableError) return ACCESS_SERVICE_UNREACHABLE;
  return LOAN_TABLE_GENERIC_FAILURE;
}

function describeLoanTableLock(lock: LockReason): FailureDescription {
  switch (lock.kind) {
    case 'refused':
      return describeLoanTableRefusal(lock.reason);
    case 'expired':
      return LOAN_TABLE_TIME_OVER;
    case 'unreachable':
      return LOAN_TABLE_CONNECTION_LOST;
    case 'aborted':
      return LOAN_TABLE_CLOSED;
  }
}
